/* VialArt — editable caption / regulatory lines for the label builder.
 *
 * Templates like Clinical QR bake fixed caption lines into their SVG
 * ("Multi-Dose Vial / For Subcutaneous Use / Store at 2-8C / For Research
 * Use Only"). This lets the customer retype or clear any of them. Kept OUT
 * of sections/vialart.liquid (which is at Shopify's section-size ceiling):
 *   - injects the caption editor into the step-2 fields when the current
 *     design actually shows these lines (detected from the live preview),
 *   - patches the preview SVG text on every render (MutationObserver, so it
 *     survives re-renders triggered by any other field),
 *   - rides the values to the order via window.vaExtraProps -> cart props ->
 *     Label Farm's _apply_caption_overrides (preview == print).
 * Research Use Only can be reworded but never blanked (compliance).
 */
(function () {
  'use strict';

  // default text baked in the templates -> {key, order-property, editable, lockNonEmpty}
  var CAPS = [
    { def: 'Multi-Dose Vial',       key: 'form',  prop: 'Caption Form',  label: 'Line 1' },
    { def: 'For Subcutaneous Use',  key: 'route', prop: 'Caption Route', label: 'Line 2 (route / use)' },
    { def: 'Store at 2-8°C',   key: 'store', prop: 'Caption Store', label: 'Storage' },
    { def: 'For Research Use Only', key: 'ruo',   prop: 'Caption RUO',   label: 'Compliance', lock: true }
  ];
  var vals = {};                 // current customer values, keyed by cap.key
  CAPS.forEach(function (c) { vals[c.key] = c.def; });

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  // Is the current design caption-capable? Two signals, either is enough:
  //  (a) a <text> node we already tagged (data-vacap) — survives the customer
  //      editing/rewording EVERY default line (incl. RUO), which would otherwise
  //      erase all the anchor strings and make us wrongly think it's not a
  //      caption design (hiding the editor + wiping the order's caption props);
  //  (b) a baked default anchor string in a fresh (un-patched) preview render.
  function previewHasCaps() {
    var pv = document.getElementById('va-flatlabel') || document.getElementById('va-flatlabel2');
    if (!pv) return false;
    if (pv.querySelector('[data-vacap]')) return true;
    var t = pv.textContent || '';
    for (var i = 0; i < CAPS.length; i++) { if (t.indexOf(CAPS[i].def) > -1) return true; }
    return false;
  }

  // Re-point the order properties the section merges at Add-to-Cart. Only send a
  // value when it differs from the default, to keep normal orders clean.
  function syncProps(active) {
    var xp = window.vaExtraProps || {};
    CAPS.forEach(function (c) { delete xp[c.prop]; });
    if (active) {
      var pres = capsPresent();                                // absent line -> no prop
      CAPS.forEach(function (c) {
        if (pres && !pres[c.key]) return;
        var v = vals[c.key];
        if (c.lock && !String(v).trim()) v = c.def;            // RUO never blank
        if (v !== c.def) xp[c.prop] = v;                       // changed -> record
      });
    }
    window.vaExtraProps = xp;
  }

  // Replace the caption <text> nodes in a preview SVG with the current values.
  function patch(root) {
    if (!root) return;
    var texts = root.querySelectorAll('text');
    for (var i = 0; i < texts.length; i++) {
      var el = texts[i], tc = (el.textContent || '');
      for (var j = 0; j < CAPS.length; j++) {
        var c = CAPS[j];
        // match either the untouched default or a value we already wrote
        if (tc === c.def || el.getAttribute('data-vacap') === c.key) {
          var v = vals[c.key];
          if (c.lock && !String(v).trim()) v = c.def;
          if (el.textContent !== v) el.textContent = v;
          el.setAttribute('data-vacap', c.key);
          break;
        }
      }
    }
  }
  function patchAll() {
    patch(document.getElementById('va-flatlabel'));
    patch(document.getElementById('va-flatlabel2'));
  }

  // Build / show / hide the editor inside the step-2 fields card.
  function ensureEditor() {
    var active = previewHasCaps();
    var box = document.getElementById('va-cap-box');
    if (!active) { if (box) box.style.display = 'none'; syncProps(false); return; }

    if (!box) {
      // anchor: the standard fields card in step 2
      var host = document.querySelector('.va-step[data-step="2"] > .va-card');
      if (!host) return;
      box = document.createElement('div');
      box.id = 'va-cap-box';
      box.className = 'va-field va-cap-box';
      var rows = CAPS.map(function (c) {
        return '<div class="va-cap-row">' +
          '<span class="va-cap-lbl">' + esc(c.label) + (c.lock ? ' <em>(required)</em>' : '') + '</span>' +
          '<input class="va-cap-inp" data-cap="' + c.key + '" type="text" maxlength="40" value="' + esc(vals[c.key]) + '"' +
          (c.lock ? ' data-lock="1"' : '') + ' placeholder="' + esc(c.def) + '" autocomplete="off" spellcheck="false">' +
          '</div>';
      }).join('');
      box.innerHTML =
        '<label>Regulatory &amp; caption lines <span class="va-cap-hint">— edit or clear any line; it prints exactly as shown</span></label>' +
        '<div class="va-cap-rows">' + rows + '</div>';
      host.appendChild(box);

      box.addEventListener('input', function (e) {
        var inp = e.target;
        if (!inp || !inp.getAttribute('data-cap')) return;
        var k = inp.getAttribute('data-cap');
        vals[k] = inp.value;
        syncProps(true);
        patchAll();
      });
      var css = document.getElementById('va-cap-css');
      if (!css) {
        css = document.createElement('style'); css.id = 'va-cap-css';
        css.textContent =
          '.va-cap-box .va-cap-hint{font-weight:500;color:var(--muted,#8b93b8);font-size:11.5px}' +
          '.va-cap-rows{display:flex;flex-direction:column;gap:7px;margin-top:4px}' +
          '.va-cap-row{display:flex;align-items:center;gap:9px}' +
          '.va-cap-lbl{flex:0 0 128px;font-size:11.5px;color:var(--muted,#8b93b8);text-align:right}' +
          '.va-cap-lbl em{color:#9a8cff;font-style:normal;font-weight:700}' +
          '.va-cap-inp{flex:1;min-width:0;font-family:inherit;font-size:13.5px;padding:8px 10px;border-radius:9px;border:1px solid var(--line,#2b2748);background:rgba(124,77,255,.05);color:inherit}' +
          '.va-cap-inp:focus{border-color:#7c4dff;outline:none}' +
          '@media(max-width:560px){.va-cap-row{flex-direction:column;align-items:stretch;gap:2px}.va-cap-lbl{flex:none;text-align:left}}';
        document.head.appendChild(css);
      }
    }
    box.style.display = '';
    // Show ONLY the lines this design actually contains — an input for a line
    // the template doesn't have prints nothing (owner 2026-07-24: "issue is
    // unused lines"). Fallback: if detection fails, show everything (old behavior).
    var pres = capsPresent();
    box.querySelectorAll('.va-cap-row').forEach(function (row) {
      var inp = row.querySelector('input[data-cap]');
      var k = inp && inp.getAttribute('data-cap');
      row.style.display = (pres && k && !pres[k]) ? 'none' : '';
    });
    syncProps(true);
    patchAll();
  }

  // Observe the preview so we re-patch after ANY re-render (compound/dose/QR/…),
  // and re-evaluate the editor when the design changes.
  function watch(id) {
    var el = document.getElementById(id);
    if (!el) return;
    var mo = new MutationObserver(function () {
      if (mo._busy) return;
      mo._busy = true;
      try { if (previewHasCaps()) patch(el); ensureEditor(); } finally { mo._busy = false; }
    });
    mo.observe(el, { childList: true, subtree: true, characterData: true });
  }

  // Patch the SVG STRING at the source: the builder preview was DOM-patched,
  // but the confirm-modal proof (#va-mprev) and the cart thumbnail canvas are
  // built fresh from buildLabel() and showed the DEFAULT captions — reading as
  // "my edits didn't cart". Wrapping buildLabel fixes every surface at once.
  // (The print side was always correct: vault -> rehydrate -> renderer.)
  // A default may appear in the SVG SOURCE as a literal char OR an HTML entity
  // ("Store at 2-8°C" is stored as "Store at 2-8&#176;C" in the template), so
  // string-patching must try every encoding. Missing this made edited/cleared
  // caption lines REVERT to the default text in the confirm-modal proof (the
  // DOM patch of the on-page preview decoded entities, so the page looked
  // right — customers read it as "add to cart overwrote my text"; 2026-07-24,
  // Sean/Grapple Shield video).
  function defVariants(def) {
    var out = [def, esc(def)];
    var deg = def.replace(/°/g, '&#176;');  if (deg  !== def) out.push(deg);
    var deg2 = def.replace(/°/g, '&deg;');  if (deg2 !== def) out.push(deg2);
    var deg3 = def.replace(/°/g, '&#xb0;'); if (deg3 !== def) out.push(deg3);
    return out;
  }
  function patchString(svg) {
    if (typeof svg !== 'string') return svg;
    for (var i = 0; i < CAPS.length; i++) {
      var c = CAPS[i], v = vals[c.key];
      if (c.lock && !String(v).trim()) v = c.def;
      if (v === c.def) continue;
      var reps = defVariants(c.def);
      for (var j = 0; j < reps.length; j++) {
        svg = svg.split('>' + reps[j] + '<').join('>' + esc(v) + '<');
      }
    }
    return svg;
  }
  function hookBuildLabel() {
    var bl = window.buildLabel;
    if (typeof bl !== 'function' || bl._vaCapWrapped) return;
    var w = function () { return patchString(bl.apply(this, arguments)); };
    w._vaCapWrapped = 1;
    w._vaCapOrig = bl;          // unpatched renderer — used for per-design line detection
    window.buildLabel = w;
  }

  // Which caption lines does the CURRENT design actually contain? (2026-07-24,
  // owner repro: Clinical QR has no "For Subcutaneous Use" line, but the editor
  // offered "Line 2 (route / use)" anyway — typed text silently went nowhere.)
  // Detect against an UNPATCHED render so customer edits can't erase the anchors,
  // and cache per design key.
  var _presCache = {};
  function capsPresent() {
    var d = window.sel;
    if (!d || !d.key) return null;
    if (_presCache[d.key]) return _presCache[d.key];
    try {
      var bl = window.buildLabel;
      var orig = (bl && bl._vaCapOrig) || bl;
      if (typeof orig !== 'function') return null;
      var svg = orig(d, (typeof window.fields === 'function' ? window.fields() : {}), true);
      if (typeof svg !== 'string' || svg.indexOf('<svg') < 0) return null;
      var pres = {};
      CAPS.forEach(function (c) {
        pres[c.key] = defVariants(c.def).some(function (v) { return svg.indexOf('>' + v + '<') > -1; });
      });
      _presCache[d.key] = pres;
      return pres;
    } catch (_) { return null; }
  }

  function boot() {
    window.vaExtraProps = window.vaExtraProps || {};
    hookBuildLabel();
    watch('va-flatlabel');
    watch('va-flatlabel2');
    ensureEditor();
    // a few settle passes for late-hydrating designs
    setTimeout(ensureEditor, 600);
    setTimeout(ensureEditor, 1800);
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
